<template>
  <div class="page">
    <div v-if="loaded">
      <div class="bg-grey-lighter lg:pt-28">
        <div class="container pb-4">
          <Back
            :to="'/dashboard'"
            :title="$t('financial_area.self')"
            :white="false"
            :textGrey="true"
          />
          <div class="hidden">
            <div id="popup-card">
              <div class="text-sm font-medium text-gray-50"></div>
              <div
                v-for="(item, index) in globalSectors.projects"
                v-bind:key="index"
                class="mb-4 mt-4 border-b-2 border-gray-50"
                :class="{ 'mb-0 border-b-0': index === globalSectors.length - 1 }"
              >
                <div class="mb-1 text-sm font-bold text-grey-2">{{ item.title }}</div>
                <div v-if="!item.multipleAmounts" class="my-4 flex justify-between">
                  <div class="text-sm text-grey-3">
                    {{ $t('financial_area.overview.charts.popup.total_amount') }}
                  </div>
                  <div class="text-sm text-grey-2">${{ item.amount }}</div>
                </div>
                <div v-for="(subItem, index) in item.multipleAmounts" v-else :key="index">
                  <div class="my-4 flex justify-between">
                    <div class="text-sm text-grey-3">{{ subItem.subTitle }}</div>
                    <div class="text-sm text-grey-2">${{ subItem.amount }}</div>
                  </div>
                </div>
                <div v-if="item.projects_number > 0" class="mb-4 flex justify-between">
                  <div class="text-sm text-grey-3">
                    {{ $t('financial_area.overview.charts.popup.projects_number') }}
                  </div>
                  <div class="text-sm text-grey-2">
                    <span class="font-bold">{{ item.projects_number }}</span
                    >/{{ averages.projects_invested }}
                  </div>
                </div>
              </div>
              <div
                v-if="
                  globalSectors.key &&
                  globalSectors.key !== 'assets' &&
                  globalSectors.arrears_projects.length > 0
                "
              >
                <div
                  v-for="(item, index) in globalSectors.arrears_projects"
                  :key="index"
                  class="mb-4 flex justify-between"
                >
                  <div class="text-sm text-grey-4">{{ item?.project?.name }}</div>
                  <div class="text-sm text-grey-4">
                    <span v-if="globalSectors.key === 'earnings'"
                      >${{ dollarFormatting(item.interest_gross_sum) }}</span
                    >
                    <span v-else>${{ dollarFormatting(item.capital_reimbursement_sum) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="averages"
            class="mt-6 grid grid-cols-2 justify-items-start gap-x-8 gap-y-8 text-left lg:mt-8 lg:grid-cols-4 lg:gap-x-14 lg:gap-y-0"
          >
            <div class="w-full">
              <div class="flex items-start justify-between">
                <span class="mr-0.5 text-sm font-normal text-grey-3 lg:mr-0">
                  {{ $t('financial_area.grid.interest_rate') }}
                </span>
                <InfoTooltip
                  :copy="'financial_area.grid.interest_rate_info'"
                  class="mt-0.5 shrink-0"
                />
              </div>
              <span class="mt-2 text-lg font-bold text-grey-2"
                >{{ roundToOneDecimal(averages.avg_interest_rate) }}%</span
              >
            </div>
            <!-- <div class="w-full">
                <div class="flex items-start justify-between">
                  <p class="text-sm font-normal text-grey-3 mr-0.5 lg:mr-0">
                    {{ $t('financial_area.grid.profitability') }}
                  </p>
                  <InfoTooltip :copy="'financial_area.grid.profitability_info'" :positionXAxisMobile="'-left-72'" class="shrink-0 mt-0.5" />
                </div>
                <span class="text-grey-2 font-bold text-lg mt-2">{{ roundToOneDecimal(averages.anual_return) }}%</span>
              </div> -->
            <div class="w-full">
              <div class="flex items-start justify-around">
                <p class="mr-0.5 text-sm font-normal text-grey-3 lg:mr-0">
                  {{ $t('financial_area.grid.projects') }}
                </p>
                <InfoTooltip
                  :copy="'financial_area.grid.projects_info'"
                  :positionXAxisMobile="'-left-72'"
                  class="mt-0.5 shrink-0"
                />
              </div>
              <span class="mt-2 text-lg font-bold text-grey-2">{{
                averages.projects_invested
              }}</span>
            </div>
            <div class="w-full">
              <div class="flex items-start justify-between">
                <p class="mr-0.5 text-sm font-normal text-grey-3 lg:mr-0">
                  {{ $t('financial_area.grid.vouchers') }}
                </p>
                <InfoTooltip :copy="'financial_area.grid.vouchers_info'" class="mt-0.5 shrink-0" />
              </div>
              <span class="mt-2 text-lg font-bold text-grey-2"
                >${{ dollarFormatting(averages.voucher_value) }}</span
              >
            </div>
              <div v-if="isBeta && isValidXirr(averages.xirr)" class="w-full">
                <div class="flex items-start justify-between">
                  <p class="text-sm font-normal text-grey-3 mr-0.5 lg:mr-0">
                    {{ $t('financial_area.grid.xirr') }}
                  </p>
                  <InfoTooltip copy="financial_area.grid.xirr_info" class="flex-shrink-0 mt-0.5" />
                </div>
                <span class="text-lg font-bold text-grey-2 mt-2">
                  {{ formatDecimalToPercentage(averages.xirr) }}
                </span>
              </div>
          </div>
        </div>
      </div>
      <div class="container">
        <div class="bg-green-8 pt-6 lg:pt-8">
          <TabsStatic v-model:current="currentTab" labels="financial_area.tabs" :nTabs="3" />
          <div class="mb-8 mt-7 block lg:mt-8 lg:grid lg:grid-cols-3 lg:gap-x-0.5 2xl:gap-x-4">
            <div class="lg:col-span-1">
              <p class="mb-4 text-left text-sm font-semibold text-grey-4">
                {{ $t('financial_area.overview.self') }}
              </p>
              <swiper
                class="mt-6 h-60 rounded-2xl lg:mt-0 lg:h-full lg:overflow-x-hidden"
                :loop="globalSectors.key !== 'assets' ? true : false"
                :pagination="true"
                :speed="400"
                :space-between="50"
                :autoplay="false"
                :observer="true"
                :observe-parents="true"
                :modules="modules"
                :slidesPerView="1"
                :breakpoints="{
                  1024: {
                    slidesPerView: 1,
                    centeredSlides: false,
                    autoHeight: false,
                  },
                }"
                @swiper="setSwiper"
              >
                <swiper-slide
                  class="relative grid grid-cols-2 gap-x-16 overflow-x-hidden bg-white lg:grid-cols-1 lg:grid-rows-doughnut lg:justify-items-center"
                >
                  <DoughnutChart
                    v-if="sectors.length > 0"
                    :sectors="sectors"
                    :globalSectors="globalSectors"
                  />
                  <div class="p-5 lg:pb-2">
                    <div
                      v-for="(item, index) in sectors"
                      :key="item.key"
                      class="mb-5"
                      :class="{ 'lg:mt-5': index === 0 }"
                    >
                      <p class="text-left text-lg font-bold text-grey-2">
                        ${{ dollarFormatting(item.value) }}
                      </p>
                      <div class="flex items-center">
                        <div
                          :id="'highlight-ball-' + index"
                          class="graph-hover mr-1.5 h-2 w-2 rounded-full"
                          :style="'background-color:' + item.color"
                        />
                        <p
                          :id="'highlight-text-' + index"
                          class="graph-hover break-words text-xs text-grey-3 transition duration-300 ease-in-out"
                        >
                          {{ item.label }}
                        </p>
                      </div>
                    </div>
                  </div>
                  <a
                    v-if="globalSectors.key !== 'assets'"
                    class="absolute bottom-2 right-4 motion-safe:hover:scale-110"
                    @click="swiperSlideNext()"
                    ><img src="@/assets/svg/chev-right-dark.svg" alt="Right Arrow"
                  /></a>
                </swiper-slide>
                <swiper-slide
                  v-if="globalSectors.key !== 'assets'"
                  class="lg:grid-columns-1 relative overflow-x-hidden rounded-2xl bg-white py-8 lg:grid"
                  :class="{ 'lg:grid-columns-2 flex': globalSectors.key === 'earnings' }"
                >
                  <div
                    class="pl-5 text-left lg:w-full lg:pl-11"
                    :class="{
                      'pl-32 pt-12 lg:place-self-center lg:pt-0': globalSectors.key === 'principal',
                      'w-1/2': globalSectors.key === 'earnings',
                    }"
                  >
                    <p
                      class="text-lg font-bold text-grey-2 lg:pt-0"
                      :class="{ 'pt-24': globalSectors.key === 'earnings' }"
                    >
                      {{ globalSectors.percentagePaidOnTime }}%
                    </p>
                    <p class="text-xs text-grey-3 lg:mt-3">
                      {{ globalSectors.percentagePaidOnTimeCopy }}
                    </p>
                    <Link
                      :text="$t('financial_area.more_details')"
                      textStyle="text-sm text-orange-1 font-bold"
                      @click="openPopUp"
                    >
                      <template #singleImage>
                        <img src="@/assets/svg/chev-right-yellow.svg" alt="right" />
                      </template>
                    </Link>
                  </div>
                  <div
                    v-if="globalSectors.key === 'earnings'"
                    class="w-1/2 border-l-2 border-gray-100 pl-5 lg:w-full lg:border-l-0 lg:border-t-2 lg:pl-0"
                  >
                    <p
                      class="pt-24 text-left text-lg font-bold text-grey-2 lg:mt-14 lg:pl-11 lg:pt-0"
                    >
                      ${{ dollarFormatting(globalSectors.future) }}
                    </p>
                    <p
                      class="text-left text-xs text-grey-3 lg:mt-3 lg:pl-11"
                      v-html="$t(globalSectors.expectedCopy)"
                    ></p>
                  </div>
                  <a
                    v-if="swiper"
                    class="absolute bottom-2 left-4 motion-safe:hover:scale-110"
                    @click="swiperSlidePrev()"
                    ><img src="@/assets/svg/chev-left-dark.svg" alt="Left Arrow" />
                  </a>
                </swiper-slide>
              </swiper>
            </div>
            <div class="my-6 w-full md:col-span-2 lg:my-0">
              <p class="mb-4 text-left text-sm font-semibold text-grey-4">
                {{ $t('financial_area.overview.performance.self') }}
              </p>
              <div class="h-96 rounded-2xl bg-transparent p-4 lg:h-full lg:bg-white">
                <!-- <TabsGrey
                labels="financial_area.overview.performance.tabs"
                :labelsData="$tm('financial_area.overview.performance.tabs')"
                v-model:current="performanceTab" 
              /> -->
                <div
                  class="mt-4 grid"
                  :class="
                    currentYear === 2019
                      ? 'grid-cols-2 justify-items-end'
                      : 'grid-cols-3 justify-items-center'
                  "
                >
                  <img
                    src="@/assets/svg/chev-left.svg"
                    alt="Left Arrow"
                    class="cursor-pointer justify-self-start"
                    @click="changeYear"
                  />
                  <p class="text-sm font-medium text-grey-4">
                    {{ $t('wording.in') }} {{ currentYear }}
                  </p>
                  <img
                    v-if="currentYear < new Date().getFullYear()"
                    src="@/assets/svg/arrows/right_grey_arrow.svg"
                    alt="Right Arrow"
                    class="cursor-pointer justify-self-end"
                    @click="changeYear(false)"
                  />
                </div>
                <div v-if="dataToShow" v-show="!loading" class="mt-4 h-80 text-left lg:h-full">
                  <p v-if="currentTab === 2" class="mt-1 text-xs font-medium text-grey-3">
                    {{ globalSectors.graphicTotal }}
                    {{ $t('financial_area.overview.charts.this_year') }}
                  </p>
                  <div class="mt-1 flex justify-between">
                    <p class="text-2xl font-bold text-grey-2">
                      <span v-if="currentTab === 2">{{
                        '$' + 
                        roundNumber(
                          timeBarView.reduce((a, b) => a + b.data.reduce((a, b) => a + b, 0), 0),
                          2
                        )
                          .toString()
                      }}</span>
                    </p>
                    <!-- <p class="mt-1 text-sm font-semibold" :class="improvement ? 'text-green-1' : 'text-red-primary'">
                    <span v-if="improvement">+</span>
                    <span v-else>-</span>
                    123€
                    <span class="text-xs text-grey-3 font-normal">{{ $t('financial_area.overview.charts.this_year') }}</span>
                  </p> -->
                  </div>
                  <BarChart
                    v-if="barChartVisible && globalSectors.key !== 'assets'"
                    class="mt-4"
                    :timeView="timeBarView"
                  />
                  <LineChart v-else class="mt-4" :timeView="timeLineView" />
                  <div
                    v-if="globalSectors.key !== 'assets'"
                    class="mt-5 flex-col justify-between lg:mt-16 xl:flex"
                  >
                    <div class="flex justify-start">
                      <div v-for="item in sectors" :key="item.key" class="mb-5 mr-5">
                        <div
                          v-if="
                            currentTab !== 3 &&
                            item.key !== 'outstanding' &&
                            item.key != 'in_arrears'
                          "
                          class="flex items-center"
                        >
                          <div
                            class="mr-1.5 h-2 w-2 rounded-full"
                            :style="'background-color:' + item.color"
                          />
                          <p class="text-xs text-grey-3">{{ item.label }}</p>
                        </div>
                      </div>
                    </div>
                    <div class="flex justify-start">
                      <a class="flex justify-around" @click="toggleBarChart">
                        <span
                          :class="{
                            'text-orange-1': globalSectors.key === 'principal',
                            'text-blue-sky-1': globalSectors.key === 'earnings',
                          }"
                          class="mr-1 text-right text-sm"
                        >
                          <span v-if="barChartVisible"> {{ $t('financial_area.over_time') }}</span>
                          <span v-else> {{ $t('financial_area.detail') }}</span>
                        </span>
                        <img
                          v-if="barChartVisible && globalSectors.key === 'principal'"
                          src="@/assets/svg/graphs/line.svg"
                          class="h-8 w-8"
                        />
                        <img
                          v-else-if="barChartVisible && globalSectors.key === 'earnings'"
                          src="@/assets/svg/graphs/line_blue.svg"
                          class="h-8 w-8"
                        />
                        <img
                          v-else-if="!barChartVisible && globalSectors.key === 'earnings'"
                          src="@/assets/svg/graphs/bar_blue.svg"
                          class="h-8 w-8"
                        />
                        <img
                          v-else-if="!barChartVisible && globalSectors.key === 'principal'"
                          src="@/assets/svg/graphs/bar.svg"
                          class="h-8 w-8"
                        />
                      </a>
                    </div>
                  </div>
                </div>
                <div
                  v-else
                  class="mt-36 flex items-center justify-center text-sm font-normal text-grey-2"
                >
                  <span v-if="globalSectors.key === 'assets' && currentYear <= 2020">{{
                    $t('financial_area.disclaimer_assets')
                  }}</span>
                  <span v-else>{{ $t('financial_area.no_data') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Back from '@/components/general/Back.vue'
import BarChart from '@/components/financial-area/BarChart.vue'
import DoughnutChart from '@/components/financial-area/DoughnutChart.vue'
import InfoTooltip from '@/components/general/InfoTooltip.vue'
import LineChart from '@/components/financial-area/LineChart.vue'
import Link from '@/components/general/Link.vue'
import TabsStatic from '@/components/general/TabsStatic.vue'
import { useI18n } from 'vue-i18n'
import { ref, computed, onMounted, watch, nextTick, toRaw } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import useGlobalHelpers from '@/mixins/useGlobalHelpers.js'
import { useStore } from 'vuex'
import SwiperCore, { Pagination } from 'swiper'
import useIsBeta from '@/composables/useIsBeta'

SwiperCore.use([Pagination])

export default {
  components: {
    Back,
    BarChart,
    DoughnutChart,
    InfoTooltip,
    LineChart,
    Link,
    TabsStatic,
    Swiper,
    SwiperSlide,
  },
  setup() {
    const i18n = useI18n({ useScope: 'global' })
    const currentTab = ref(1)
    const { dollarFormatting, roundToOneDecimal, roundNumber, formatDecimalToPercentage } = useGlobalHelpers()
    const loaded = ref(false)
    const barChartVisible = ref(true)
    const store = useStore()
    const swiper = ref(null)
    const currentYear = ref(new Date().getFullYear())
    const isBeta =  useIsBeta()

    const profile = computed(() => {
      return store.getters.getProfile()
    })

    const loading = computed(() => {
      return store.state.general.loading
    })

    const swiperSlideNext = () => {
      swiper.value?.slideNext()
    }

    const swiperSlidePrev = () => {
      swiper.value?.slidePrev()
    }

    const averages = computed(() => {
      return store.state.financial.averages
    })

    const sectors = computed(() => {
      if (loaded.value) {
        if (currentTab.value === 3) {
          return [
            {
              key: 'outstanding',
              label: i18n.t('financial_area.overview.charts.assets.outstanding'),
              value: store.state.financial.totalAssets.assets_balance,
              color: 'rgba(54, 147, 123, 1)',
            },
            {
              key: 'available',
              label: i18n.t('financial_area.overview.charts.assets.available'),
              value: store.state.financial.totalAssets.wallet_balance,
              color: 'rgba(187, 237, 213, 1)',
            },
          ]
        } else if (currentTab.value === 2) {
          return [
            {
              key: 'net_interest',
              label: i18n.t('financial_area.overview.charts.earnings.net'),
              value: store.state.financial.totalEarning.total_paid_interest_gross,
              color: 'rgba(43, 131, 192, 1)',
            },
          ]
        } else {
          return [
            {
              key: 'paid_back',
              label: i18n.t('financial_area.overview.charts.principal.paid_back'),
              value: store.state.financial.totalPrincipal.total_paid_back,
              color: 'rgb(184, 142, 89, 1)',
            },
            {
              key: 'outstanding',
              label: i18n.t('financial_area.overview.charts.principal.outstanding'),
              value: store.state.financial.totalPrincipal.total_outstanding,
              color: 'rgba(241, 236, 223, 1)',
            },
            {
              key: 'in_arrears',
              label: i18n.t('financial_area.overview.charts.principal.arrears'),
              value: store.state.financial.totalPrincipal.total_in_arrears,
              color: 'rgb(224, 217, 217, 1)',
            },
          ]
        }
      }
      return []
    })

    const globalSectors = computed(() => {
      if (loaded.value) {
        if (currentTab.value === 3) {
          return {
            key: 'assets',
            graphicTotal: i18n.t('financial_area.overview.charts.assets.total'),
          }
        } else if (currentTab.value === 2) {
          return {
            key: 'earnings',
            graphicTotal: i18n.t('financial_area.overview.charts.earnings.total'),
            percentagePaidOnTime: store.state.financial.totalEarning.percentage_paid_on_time,
            percentagePaidOnTimeCopy: i18n.t('financial_area.overview.charts.percentage_on_time'),
            future: store.state.financial.totalEarning.future_interest,
            expectedCopy: i18n.t('financial_area.overview.charts.earnings.expected'),
            projects: [
              {
                title: i18n.t('wording.paid'),
                amount: dollarFormatting(
                  store.state.financial.totalEarning.total_paid_interest_gross
                ),
              },
              {
                title: i18n.t('financial_area.overview.charts.earnings.expected'),
                amount: dollarFormatting(store.state.financial.totalEarning.future_interest),
              },
              {
                title: i18n.t('financial_area.overview.charts.principal.arrears'),
                amount: dollarFormatting(
                  store.state.financial.totalEarning.details.arrears.reduce(
                    (a, b) => a + b.interest_gross_sum,
                    0
                  )
                ),
                projects_number: store.state.financial.totalEarning.details.arrears.length,
              },
            ],
            arrears_projects: store.state.financial.totalEarning.details.arrears,
          }
        } else {
          return {
            key: 'principal',
            graphicTotal: i18n.t('financial_area.overview.charts.principal.total'),
            percentagePaidOnTime: store.state.financial.totalPrincipal.percentage_paid_on_time,
            percentagePaidOnTimeCopy: i18n.t('financial_area.overview.charts.percentage_on_time'),
            projects: [
              {
                title: i18n.t('wording.settled'),
                amount: dollarFormatting(
                  store.state.financial.totalPrincipal.details.liquidated.reduce(
                    (a, b) => a + b.capital_reimbursement_sum,
                    0
                  )
                ),
                projects_number: store.state.financial.totalPrincipal.details.liquidated.length,
              },
              {
                title: i18n.t('financial_area.overview.charts.principal.outstanding'),
                multipleAmounts: [
                  {
                    subTitle:
                      i18n.t('financial_area.overview.charts.popup.total_amount') +
                      ' ' +
                      i18n.t('financial_area.overview.charts.principal.outstanding'),
                    amount: dollarFormatting(store.state.financial.totalPrincipal.total_outstanding),
                  },
                  {
                    subTitle:
                      i18n.t('financial_area.overview.charts.popup.total_amount') +
                      ' ' +
                      i18n.t('financial_area.overview.charts.principal.paid_back'),
                    amount: dollarFormatting(store.state.financial.totalPrincipal.total_paid_back),
                  },
                ],
                projects_number: store.state.financial.totalPrincipal.details.in_debt.length,
              },
              {
                title: i18n.t('financial_area.overview.charts.principal.arrears'),
                amount: dollarFormatting(store.state.financial.totalPrincipal.total_in_arrears),
                projects_number: store.state.financial.totalPrincipal.details.arrears.length,
              },
            ],
            arrears_projects: store.state.financial.totalPrincipal.details.arrears,
          }
        }
      }
      return []
    })

    function setSwiper(value) {
      swiper.value = value

      const ro = new ResizeObserver(() => {
        swiper.value.update()
      })
      ro.observe(swiper.value.slides[swiper.value.activeIndex])
    }

    const openPopUp = () => {
      const content = document.getElementById('popup-card')
      const popup = {
        title:
          currentTab.value === 1
            ? i18n.t('financial_area.overview.charts.popup.principal')
            : i18n.t('financial_area.overview.charts.popup.earnings'),
        align: 'left',
        side: true,
        content: content.outerHTML,
      }
      store.dispatch('setPopup', popup)
    }

    const toggleBarChart = async () => {
      await nextTick()
      barChartVisible.value = !barChartVisible.value
    }

    const timeBarView = computed(() => {
      if (loaded.value) {
        if (
          currentTab.value === 2 &&
          store.state.financial?.yearlyEarnings &&
          store.state.financial?.yearlyEarnings[currentYear.value]
        ) {
          const arrayEarnings = new Array(12).fill(0)

          for (const [key, value] of Object.entries(
            store.state.financial?.yearlyEarnings[currentYear.value]
          )) {
            let valueEarnings = 0
            if (value) {
              // eslint-disable-next-line
              for (const [for_key, for_value] of Object.entries(value)) {
                valueEarnings = for_value.gross ?? 0
              }
            }
            arrayEarnings[parseInt(key) - 1] = valueEarnings / 100
          }

          return [
            {
              data: arrayEarnings,
              backgroundColor: 'rgba(43, 131, 192, 1)',
              title: 'financial_area.overview.charts.earnings.net',
            },
          ]
        } else if (
          currentTab.value === 1 &&
          store.state.financial?.yearlyPrincipal &&
          store.state.financial?.yearlyPrincipal[currentYear.value]
        ) {
          const arrayArrears = new Array(12).fill(0)
          const arrayToPay = new Array(12).fill(0)
          const arrayReimbursement = new Array(12).fill(0)
          let valueArrears = 0
          let valueToPay = 0
          let valueReimbursement = 0

          for (const [key, value] of Object.entries(
            store.state.financial.yearlyPrincipal[currentYear.value]
          )) {
            if (value) {
              // eslint-disable-next-line
              for (const [for_key, for_value] of Object.entries(value)) {
                valueArrears = for_value.arrears
                valueToPay = for_value.to_pay
                valueReimbursement = for_value.reimbursment
              }
            }
            arrayArrears[parseInt(key) - 1] = valueArrears / 100
            arrayToPay[parseInt(key) - 1] = valueToPay / 100
            arrayReimbursement[parseInt(key) - 1] = valueReimbursement / 100
          }

          return [
            {
              data: arrayReimbursement,
              backgroundColor: 'rgb(184, 142, 89, 1)',
              title: 'financial_area.overview.charts.principal.paid_back',
            },
            // {
            //   data: arrayToPay,
            //   backgroundColor: 'rgba(241, 236, 223, 1)',
            //   title: 'financial_area.overview.charts.principal.outstanding'
            // },
            // {
            //   data: arrayArrears,
            //   backgroundColor: 'rgb(224, 217, 217, 1)',
            //   title: 'financial_area.overview.charts.principal.arrears'
            // }
          ]
        }
      }
      return []
    })

    const dataToShow = computed(() => {
      if (
        currentTab.value === 3 &&
        store.state.financial?.yearlyAssets[currentYear.value]?.length === 0
      )
        return false
      else if (
        currentTab.value === 2 &&
        store.state.financial?.yearlyEarnings[currentYear.value]?.length === 0
      )
        return false
      else if (
        currentTab.value === 1 &&
        store.state.financial?.yearlyPrincipal[currentYear.value]?.length === 0
      )
        return false
      return true
    })

    const timeLineView = computed(() => {
      if (loaded.value) {
        const currentDate = new Date()
        const isCurrentYear = currentYear.value === currentDate.getFullYear()
        const currentMonth = isCurrentYear ? new Date().getMonth() + 1 : 12

        if (
          currentTab.value === 3 &&
          store.state.financial?.yearlyAssets &&
          store.state.financial?.yearlyAssets[currentYear.value]
        ) {
          const arrayAssets = new Array(currentMonth).fill(0)
          const transactions = toRaw(store.state.financial.yearlyAssets[currentYear.value])

          // Opening balance carried from before this year, so January doesn't render
          // as a hard reset to zero when the account already held assets.
          const firstMonthWithData = Object.keys(transactions).sort((a, b) => a - b)[0]
          let openingBalance = transactions[firstMonthWithData]?.[0]?.line_graph_start?.assets_balance ?? 0
          openingBalance = openingBalance > 0 ? openingBalance / 100 : 0

          let i = 1
          arrayAssets.forEach(() => {
            // If its the current year and the current month its the most updated value - the one in the doughnut
            if (isCurrentYear && i === currentMonth) {
              arrayAssets[i - 1] =
                (store.state.financial.totalAssets.assets_balance +
                  store.state.financial.totalAssets.wallet_balance) /
                100
            }

            if (i < 10) i = ('0' + i).slice(-2)

            if (transactions[i]) {
              arrayAssets[i - 1] = transactions[i][0].assets_balance
                ? transactions[i][0].assets_balance / 100
                : (arrayAssets[i - 1] ?? 0)
            } else if (i !== ('0' + currentMonth).slice(-2)) {
              arrayAssets[i - 1] = arrayAssets[i - 2] ?? openingBalance
            }
            i++
          })

          return [
            {
              data: arrayAssets,
              borderColor: 'rgba(26, 147, 118, 1)',
              pointStyle: 'line',
              pointRadius: 2,
              title: 'financial_area.overview.charts.assets.total',
              fill: {
                target: 'start',
                above: 'rgba(229, 242, 239, 0.4)', // Area color below line
              },
            },
          ]
        } else if (
          currentTab.value === 2 &&
          store.state.financial?.yearlyEarnings &&
          store.state.financial?.yearlyEarnings[currentYear.value]
        ) {
          const arrayEarnings = new Array(12).fill(0)
          const current = timeBarView.value[0]

          const firstMonthPayments = Object.keys(
            store.state.financial?.yearlyEarnings[currentYear.value]
          ).sort((a, b) => a - b)[0]

          arrayEarnings.forEach((element, index) => {
            // If we are going over the month we can stop
            if (index + 1 > currentMonth) arrayEarnings[index] = null
            else if (index + 1 <= parseInt(firstMonthPayments)) {
              // starting value - All the payments until the first month of payment of that year - starting line of line graph
              let startingValue = 0
              startingValue =
                store.state.financial?.yearlyEarnings[currentYear.value][firstMonthPayments][0]
                  .line_graph_start?.net ?? 0
              startingValue = startingValue > 0 ? startingValue / 100 : 0
              // If there is a value this month we will add it to the sum of last year, if not the value should be last year's sum
              arrayEarnings[index] = current.data[index]
                ? current.data[index] + startingValue
                : startingValue
            } else {
              arrayEarnings[index] = current.data[index]
                ? current.data[index] + (arrayEarnings[index - 1] ?? 0)
                : (arrayEarnings[index - 1] ?? 0)
            }
          })

          return [
            {
              data: arrayEarnings,
              borderColor: 'rgba(44, 131, 192, 1)',
              pointStyle: 'line',
              pointRadius: 2,
              title: 'financial_area.overview.charts.earnings.net',
              fill: {
                target: 'stack',
                above: 'rgba(232, 241, 246, 0.4)',
              },
            },
          ]
        } else if (
          currentTab.value === 1 &&
          store.state.financial?.yearlyPrincipal &&
          store.state.financial?.yearlyPrincipal[currentYear.value]
        ) {
          // let arrayArrears = new Array(12).fill(0)
          // let arrayToPay = new Array(12).fill(0)
          const arrayReimbursement = new Array(12).fill(0)

          // eslint-disable-next-line
          timeBarView.value.map((current, index, arr) => {
            const firstMonthPayments = Object.keys(
              store.state.financial?.yearlyPrincipal[currentYear.value]
            ).sort((a, b) => a - b)[0]

            if (index === 0) {
              arrayReimbursement.forEach((element, index) => {
                // If we are going over the month we can stop
                if (index + 1 > currentMonth) arrayReimbursement[index] = null
                else if (index + 1 <= parseInt(firstMonthPayments)) {
                  // starting value - All the payments until the first month of payment of that year - starting line of line graph
                  let startingValue = 0
                  startingValue =
                    store.state.financial?.yearlyPrincipal[currentYear.value][firstMonthPayments][0]
                      .line_graph_start?.reimbursment ?? 0
                  startingValue = startingValue > 0 ? startingValue / 100 : 0
                  // If there is a value this month we will add it to the sum of last year, if not the value should be last year's sum
                  arrayReimbursement[index] = current.data[index]
                    ? current.data[index] + startingValue
                    : startingValue
                } else {
                  arrayReimbursement[index] = current.data[index]
                    ? current.data[index] + (arrayReimbursement[index - 1] ?? 0)
                    : (arrayReimbursement[index - 1] ?? 0)
                }
              })
            }
          })

          return [
            {
              data: arrayReimbursement,
              borderColor: 'rgb(184, 142, 89, 1)',
              pointStyle: 'line',
              pointRadius: 2,
              title: 'financial_area.overview.charts.principal.paid_back',
              fill: {
                target: 'origin',
                above: 'rgba(239, 232, 218, 0.4)',
              },
            },
            // {
            //   data: arrayToPay,
            //   borderColor: 'rgba(241, 236, 223, 1)',
            //   pointStyle: 'line',
            //   title: 'financial_area.overview.charts.principal.outstanding'
            // },
            // {
            //   data: arrayArrears,
            //   borderColor: 'rgb(224, 217, 217, 1)',
            //   pointStyle: 'line',
            //   title: 'financial_area.overview.charts.principal.arrears'
            // }
          ]
        }
      }
      return []
    })

    const isValidXirr = (value) => { 
      return typeof value === 'number' && !isNaN(value) && isFinite(value) && Math.sign(value) !== -1
    } 

    const changeYear = (minus = true) => {
      if (minus) {
        currentYear.value = currentYear.value - 1
      } else {
        currentYear.value = currentYear.value + 1
      }
    }

    onMounted(async () => {
      store.dispatch('setLoading', true)
      if (
        !store.state.financial?.yearlyPrincipal ||
        !store.state.financial?.yearlyPrincipal[currentYear.value]
      ) {
        await store.dispatch('fetchYearly', { year: currentYear.value, type: 'principal' })
      }

      if (!store.state.financial?.averages) {
        await store.dispatch('fetchTotals')
      }

      store.dispatch('setLoading', false)
      loaded.value = true
    })

    watch(
      () => [currentTab.value, currentYear.value],
      async ([newValue, newYear]) => {
        if (newValue || newYear) {
          loaded.value = false
          store.dispatch('setLoading', true)
          swiper.value.slideTo(1)
          let typeToSearch = 'principal'
          if (newValue === 2) {
            typeToSearch = 'earnings'
          } else if (newValue === 3) {
            typeToSearch = 'assets'
          }
          await store.dispatch('fetchYearly', { year: newYear, type: typeToSearch })
          barChartVisible.value = true
          loaded.value = true
          store.dispatch('setLoading', false)
        }
      }
    )

    return {
      averages,
      barChartVisible,
      changeYear,
      currentTab,
      currentYear,
      dataToShow,
      dollarFormatting,
      globalSectors,
      loaded,
      loading,
      modules: [Pagination],
      openPopUp,
      profile,
      roundNumber,
      roundToOneDecimal,
      sectors,
      setSwiper,
      swiper,
      swiperSlideNext,
      swiperSlidePrev,
      timeBarView,
      timeLineView,
      toggleBarChart,
      isBeta,
      isValidXirr,
      formatDecimalToPercentage
    }
  },
}
</script>

<style scoped>
:deep(.swiper-pagination) {
  text-align: center;
  pointer-events: none;
}

:deep(.swiper-pagination-bullet) {
  pointer-events: auto;
}

:deep(.swiper-pagination-bullets .swiper-pagination-bullet) {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
}

:deep(
  .swiper-pagination-fraction,
  .swiper-pagination-custom,
  .swiper-container-horizontal > .swiper-pagination-bullets
) {
  bottom: 10px !important;
  left: 25%;
  width: 50%;
}

@media (max-width: 768px) {
  :deep(
    .swiper-pagination-fraction,
    .swiper-pagination-custom,
    .swiper-container-horizontal > .swiper-pagination-bullets
  ) {
    width: 57%;
  }
}

@media (min-width: 1024px) and (max-width: 1300px) {
  :deep(
    .swiper-pagination-fraction,
    .swiper-pagination-custom,
    .swiper-container-horizontal > .swiper-pagination-bullets
  ) {
    width: 60%;
  }
}

@media (min-width: 1300px) and (max-width: 1440px) {
  :deep(
    .swiper-pagination-fraction,
    .swiper-pagination-custom,
    .swiper-container-horizontal > .swiper-pagination-bullets
  ) {
    width: 50%;
  }
}

:deep(.swiper-pagination-bullet-active) {
  background-color: #273441;
}
</style>
